import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { payroll } from '../../api';

const EMPTY = {
  employeeNumber: '', firstName: '', lastName: '', idNumber: '', passportNumber: '', taxNumber: '', dateOfBirth: '', startDate: '', endDate: '',
  jobTitle: '', email: '', cellPhone: '', address: '', payFrequency: 'monthly', payType: 'salary', basicSalary: 0, hourlyRate: 0, standardHours: 0, monthlyHours: 173.33,
  travelAllowance: 0, otherAllowance: 0, pensionEmployee: 0, pensionEmployer: 0, providentEmployee: 0, providentEmployer: 0, raContribution: 0,
  medicalAidEmployee: 0, medicalAidEmployer: 0, medicalDependants: 0, garnishee: 0, loanRepayment: 0, otherDeduction: 0,
  etiEligible: false, uifExempt: false, directiveRate: null, bankName: '', bankAccount: '', bankBranch: '', bankAccountType: 'cheque', annualLeaveDays: 15, sickLeaveDays: 30, familyLeaveDays: 3, workDaysPerWeek: 5, active: true,
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800 mb-2">{title}</div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{children}</div>
  </div>
);

export const ACCOUNT_TYPES = [['cheque', 'Cheque / Current'], ['savings', 'Savings'], ['transmission', 'Transmission'], ['bond', 'Bond'], ['credit_card', 'Credit card']];

export const EmployeeDialog = ({ open, onOpenChange, clientId, employee, onSaved, toast }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(employee ? { ...EMPTY, ...employee } : EMPTY); }, [open, employee]);

  const field = (k, label, type = 'text', extra = {}) => (
    <div key={k}>
      <Label htmlFor={`emp-${k}`} className="text-xs">{label}</Label>
      <Input id={`emp-${k}`} type={type} value={f[k] ?? ''} {...extra}
        onChange={(e) => setF({ ...f, [k]: type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value })}
        className="mt-1 h-9" data-testid={`employee-${k}-input`}/>
    </div>
  );
  const money = (k, label) => field(k, label, 'number', { min: 0, step: '0.01' });
  const check = (k, label) => (
    <label key={k} className="flex items-center gap-2 text-sm text-stone-700 col-span-2 md:col-span-1">
      <Checkbox checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: !!v })} data-testid={`employee-${k}-checkbox`}/> {label}
    </label>
  );

  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f };
    Object.keys(EMPTY).forEach((k) => { if (typeof EMPTY[k] === 'number' && body[k] === '') body[k] = 0; });
    ['dateOfBirth', 'startDate', 'endDate'].forEach((k) => { if (!body[k]) body[k] = null; });
    if (body.directiveRate === '') body.directiveRate = null;
    (employee ? payroll.updateEmployee(employee.id, body) : payroll.createEmployee(clientId, body))
      .then(() => { toast({ title: employee ? 'Employee updated' : 'Employee added' }); onOpenChange(false); onSaved(); })
      .catch((err) => toast({ title: 'Could not save employee', description: err?.response?.data?.detail?.toString() || 'Check the fields.', variant: 'destructive' }))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
        <DialogHeader><DialogTitle>{employee ? `Edit ${employee.firstName} ${employee.lastName}` : 'Add employee'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <Section title="Personal & tax">
            {field('firstName', 'First name *', 'text', { required: true })}
            {field('lastName', 'Surname *', 'text', { required: true })}
            {field('employeeNumber', 'Employee no. (auto if blank)')}
            {field('idNumber', 'SA ID number')}
            {field('passportNumber', 'Passport (if no ID)')}
            {field('taxNumber', 'Income tax number')}
            {field('dateOfBirth', 'Date of birth', 'date')}
            {field('startDate', 'Start date', 'date')}
            {field('endDate', 'End date (if left)', 'date')}
            {field('jobTitle', 'Job title')}
            {field('email', 'Email (for payslips)', 'email')}
            {field('cellPhone', 'Cell phone', 'tel', { placeholder: '082 123 4567', pattern: '^(\\+27|0)[\\d ]{9,12}$', title: 'SA number, e.g. 082 123 4567 or +27 82 123 4567' })}
            {field('address', 'Address')}
          </Section>
          <Section title="Pay">
            <div>
              <Label className="text-xs">Pay frequency</Label>
              <Select value={f.payFrequency} onValueChange={(v) => setF({ ...f, payFrequency: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="employee-payFrequency-select"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="fortnightly">Fortnightly</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Pay type</Label>
              <Select value={f.payType} onValueChange={(v) => setF({ ...f, payType: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="employee-payType-select"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="salary">Fixed salary</SelectItem><SelectItem value="hourly">Hourly wage</SelectItem></SelectContent>
              </Select>
            </div>
            {f.payType === 'salary' ? money('basicSalary', 'Basic salary per period (R)') : money('hourlyRate', 'Hourly rate (R)')}
            {f.payType === 'hourly' ? field('standardHours', 'Standard hours per period', 'number', { min: 0 }) : money('hourlyRate', 'Hourly rate for overtime (optional)')}
            {field('monthlyHours', 'Hours worked per month (for ETI)', 'number', { min: 0, step: '0.01' })}
            {field('directiveRate', 'Tax directive % (optional)', 'number', { min: 0, max: 100, step: '0.01' })}
          </Section>
          <Section title="Recurring allowances">
            {money('travelAllowance', 'Travel allowance (80% taxable)')}
            {money('otherAllowance', 'Other taxable allowances')}
          </Section>
          <Section title="Retirement & medical">
            {money('pensionEmployee', 'Pension — employee')}
            {money('pensionEmployer', 'Pension — employer')}
            {money('providentEmployee', 'Provident — employee')}
            {money('providentEmployer', 'Provident — employer')}
            {money('raContribution', 'Retirement annuity (via payroll)')}
            {money('medicalAidEmployee', 'Medical aid — employee')}
            {money('medicalAidEmployer', 'Medical aid — employer')}
            {field('medicalDependants', 'Medical aid members (incl. main)', 'number', { min: 0, max: 20 })}
          </Section>
          <Section title="Other deductions">
            {money('garnishee', 'Garnishee order')}
            {money('loanRepayment', 'Loan repayment')}
            {money('otherDeduction', 'Other deduction')}
          </Section>
          <Section title="Leave entitlements (BCEA defaults)">
            {field('annualLeaveDays', 'Annual leave days / year', 'number', { min: 0, step: '0.5' })}
            {field('sickLeaveDays', 'Sick leave days / 3-year cycle', 'number', { min: 0, step: '0.5' })}
            {field('familyLeaveDays', 'Family responsibility days / year', 'number', { min: 0, step: '0.5' })}
            {field('workDaysPerWeek', 'Working days per week', 'number', { min: 1, max: 7, step: '0.5' })}
          </Section>
          <Section title="Banking & flags">
            {field('bankName', 'Bank')}
            {field('bankAccount', 'Account number')}
            {field('bankBranch', 'Branch code')}
            <div>
              <Label className="text-xs">Account type</Label>
              <Select value={f.bankAccountType || 'cheque'} onValueChange={(v) => setF({ ...f, bankAccountType: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="employee-bankAccountType-select"><SelectValue/></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`account-type-${k}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {check('etiEligible', 'Qualifies for ETI (18–29, new hire)')}
            {check('uifExempt', 'UIF exempt (< 24 hrs/month)')}
            {check('active', 'Active employee')}
          </Section>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="employee-save-btn">{busy && <Loader2 size={14} className="mr-2 animate-spin"/>} Save employee</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
